import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';

type Permission =
  | 'candidates:read' | 'candidates:write' | 'candidates:delete'
  | 'jobs:read' | 'jobs:write' | 'jobs:delete'
  | 'pipeline:read' | 'pipeline:write'
  | 'tags:read' | 'tags:write' | 'tags:delete'
  | 'email:read' | 'email:send' | 'email:templates'
  | 'export:data'
  | 'users:read' | 'users:write'
  | 'settings:read' | 'settings:write'
  | 'companies:read' | 'companies:write' | 'companies:delete'
  | 'reports:read'
  | 'notifications:read'
  | 'placements:read' | 'placements:write'
  | 'interviews:read' | 'interviews:write'
  | 'jobboards:read' | 'jobboards:write'
  | 'notes:read' | 'notes:write' | 'notes:delete';

const PERMISSION_MAP: Record<Permission, UserRole[]> = {
  'candidates:read':    ['admin', 'account_manager', 'recruiter', 'viewer'],
  'candidates:write':   ['admin', 'account_manager', 'recruiter'],
  'candidates:delete':  ['admin', 'account_manager'],
  'jobs:read':          ['admin', 'account_manager', 'recruiter', 'viewer'],
  'jobs:write':         ['admin', 'account_manager', 'recruiter'],
  'jobs:delete':        ['admin', 'account_manager'],
  'pipeline:read':      ['admin', 'account_manager', 'recruiter', 'viewer'],
  'pipeline:write':     ['admin', 'account_manager', 'recruiter'],
  'tags:read':          ['admin', 'account_manager', 'recruiter', 'viewer'],
  'tags:write':         ['admin', 'account_manager'],
  'tags:delete':        ['admin'],
  'email:read':         ['admin', 'account_manager', 'recruiter', 'viewer'],
  'email:send':         ['admin', 'account_manager', 'recruiter'],
  'email:templates':    ['admin', 'account_manager'],
  'export:data':        ['admin', 'account_manager', 'recruiter'],
  'users:read':         ['admin'],
  'users:write':        ['admin'],
  'settings:read':      ['admin', 'account_manager', 'recruiter', 'viewer'],
  'settings:write':     ['admin'],
  'companies:read':     ['admin', 'account_manager', 'recruiter', 'viewer'],
  'companies:write':    ['admin', 'account_manager', 'recruiter'],
  'companies:delete':   ['admin', 'account_manager'],
  'reports:read':       ['admin', 'account_manager'],
  'notifications:read': ['admin', 'account_manager', 'recruiter', 'viewer'],
  'placements:read':    ['admin', 'account_manager', 'recruiter', 'viewer'],
  'placements:write':   ['admin', 'account_manager'],
  'interviews:read':    ['admin', 'account_manager', 'recruiter', 'viewer'],
  'interviews:write':   ['admin', 'account_manager', 'recruiter'],
  'jobboards:read':     ['admin', 'account_manager'],
  'jobboards:write':    ['admin'],
  'notes:read':         ['admin', 'account_manager', 'recruiter', 'viewer'],
  'notes:write':        ['admin', 'account_manager', 'recruiter'],
  'notes:delete':       ['admin', 'account_manager'],
};

export function usePermissions() {
  const { user } = useAuth();
  const role = (user?.role ?? 'viewer') as UserRole;

  const can = (permission: Permission): boolean => {
    const allowedRoles = PERMISSION_MAP[permission];
    return allowedRoles ? allowedRoles.includes(role) : false;
  };

  return { can, role };
}
